import React from "react";
import { PageHeader } from "./petaarUi";

const PETAAR_CONTACT = {
  address: "Petaar, Hsr layout, banglore, India - 560102",
  phone: "8050539777",
  email: "support@petaar.com",
};

const PAGE_CONTENT = {
  about: {
    title: "About Us",
    blocks: [
      {
        type: "paragraph",
        text:
          "Petaar is a pan-India pet care platform that connects pet parents with trusted service providers and veterinary experts.",
      },
      {
        type: "paragraph",
        text:
          "You can discover services, compare providers, book online, and get support for your pet’s health and daily care needs.",
      },
      {
        type: "paragraph",
        text: "Our goal is to make pet care safe, accessible, and reliable across India.",
      },
    ],
  },
  terms: {
    title: "Terms & Conditions",
    blocks: [
      {
        type: "paragraph",
        text: "By using Petaar, you agree to these terms.",
      },
      {
        type: "list",
        items: [
          "You must provide accurate account and booking details.",
          "Service availability, pricing, and timelines are provided by partners and may vary by location.",
          "Payments are processed through secure payment gateways.",
          "Petaar may charge platform fees or commission where applicable.",
          "Users must not misuse the platform, post false information, or violate applicable laws.",
          "Petaar may suspend accounts for fraud, abuse, or policy violations.",
          "Petaar is a platform provider and does not replace emergency veterinary care.",
          "Disputes are subject to applicable Indian laws and jurisdiction.",
        ],
      },
    ],
  },
  privacy: {
    title: "Privacy Policy",
    blocks: [
      {
        type: "paragraph",
        text: "We collect only required information to deliver services safely and efficiently.",
      },
      {
        type: "list",
        items: [
          "Data collected: name, contact details, location, booking details, payment metadata, chat or support data, and uploaded documents where required.",
          "Data use: account creation, booking fulfillment, support, fraud prevention, analytics, and service improvement.",
          "Data sharing: only with relevant providers or partners needed to complete your request, and as required by law.",
          "Security: reasonable technical and organizational safeguards are used to protect your data.",
          "Your rights: you can request profile updates, corrections, or account deletion as per applicable law.",
          "Children’s data: guardian consent is required where legally applicable.",
        ],
      },
    ],
  },
  refund: {
    title: "Refund Policy",
    blocks: [
      {
        type: "paragraph",
        text: "Refund eligibility depends on service stage and provider policy.",
      },
      {
        type: "list",
        items: [
          "Failed payment: full auto-refund or reversal as per bank or gateway timeline.",
          "Cancelled before provider confirmation: full refund.",
          "Cancelled after confirmation: partial or no refund depending on provider terms and time of cancellation.",
          "No-show after confirmed slot: generally non-refundable unless approved after review.",
          "Duplicate charge: full refund for duplicate transaction after verification.",
          "Refund timelines: usually 5 to 10 business days, bank dependent.",
        ],
      },
    ],
  },
  support: {
    title: "Support",
    blocks: [
      {
        type: "paragraph",
        text:
          "Need help with bookings, payments, provider issues, or account access?",
      },
      {
        type: "paragraph",
        text:
          "Our support team can assist with escalations, rescheduling, refunds, and technical issues.",
      },
    ],
  },
  help: {
    title: "Help Center",
    blocks: [
      {
        type: "paragraph",
        text: "Common help topics:",
      },
      {
        type: "list",
        items: [
          "Booking and cancellation",
          "Payment and refund status",
          "Provider verification and profile details",
          "Consultation flow and prescriptions",
          "Account login, OTP, and password reset",
          "AI assistant usage and limitations",
        ],
      },
    ],
  },
  medical: {
    title: "Medical Disclaimer",
    blocks: [
      {
        type: "paragraph",
        text:
          "Petaar AI and platform content are for informational purposes only and are not a substitute for professional veterinary diagnosis or treatment.",
      },
      {
        type: "paragraph",
        text: "For emergencies or serious symptoms, contact a licensed veterinarian immediately.",
      },
      {
        type: "paragraph",
        text: "Do not self-medicate pets based only on online guidance.",
      },
    ],
  },
  contact: {
    title: "Contact Us",
    blocks: [
      {
        type: "paragraph",
        text: "For support, booking help, or account assistance, contact Petaar:",
      },
      {
        type: "list",
        items: [
          `Address: ${PETAAR_CONTACT.address}`,
          `Phone / WhatsApp: +91 ${PETAAR_CONTACT.phone}`,
          `Email: ${PETAAR_CONTACT.email}`,
        ],
      },
    ],
  },
};

const renderBlock = (block, index) => {
  if (!block) return null;
  if (block.type === "list") {
    return (
      <ul key={`block-${index}`} style={{ margin: "10px 0 0", paddingLeft: 20, color: "var(--text)", lineHeight: 1.8 }}>
        {(block.items || []).map((item, itemIndex) => (
          <li key={`block-${index}-item-${itemIndex}`} style={{ marginBottom: 8 }}>
            {item}
          </li>
        ))}
      </ul>
    );
  }
  return (
    <p key={`block-${index}`} style={{ margin: 0, color: "var(--text)", lineHeight: 1.8 }}>
      {block.text}
    </p>
  );
};

export default function LegalPage({ pageKey = "terms", onBack }) {
  const page = PAGE_CONTENT[pageKey] || PAGE_CONTENT.terms;

  return (
    <div className="petaar-page">
      <PageHeader
        eyebrow="Petaar Legal"
        title={page.title}
        subtitle="Read the latest policy, platform, and compliance information for Petaar services and consultations."
        actions={
          onBack ? (
            <button className="petaar-button petaar-button--secondary" onClick={onBack}>
              Back
            </button>
          ) : null
        }
      />

      <div className="petaar-card" style={{ display: "grid", gap: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <div className="petaar-kicker">Last updated: February 25, 2026</div>
        </div>
        <div style={{ display: "grid", gap: 18 }}>
          {(page.blocks || []).map((block, index) => (
            <div key={`section-${index}`} className="petaar-list-row" style={{ padding: 18 }}>
              {renderBlock(block, index)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
